/**
 * Pests — Platform Admin страница справочника вредителей (AK-233).
 *
 * Тонкий wrapper над `ReferenceCatalogList` + `PestEditorDrawer`,
 * переиспользует reference-CRUD эталон. Delete = архивирование.
 * Дизайн: docs/superpowers/prototypes/ak226-reference-hub/index.html.
 */
import { useCallback, useMemo, useState } from 'react';
import { Archive, GitMerge, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import {
  ReferenceMergeDialog,
  type MergeCandidate,
} from '@/components/reference/ReferenceMergeDialog';
import { PestEditorDrawer } from './PestEditorDrawer';
import {
  useArchivePest,
  useCreatePest,
  useMergePests,
  usePestCategories,
  usePestsInfinite,
  useUpdatePest,
} from './hooks/useCatalogQueries';
import type { PestListItem, PestPayload, ReferenceListQuery } from './types';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'записей';
  if (m10 === 1) return 'запись';
  if (m10 >= 2 && m10 <= 4) return 'записи';
  return 'записей';
}

export function PlatformAdminPestsPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PestListItem | null>(null);

  const categoriesQ = usePestCategories();
  const categories = categoriesQ.data?.items ?? [];

  const query: ReferenceListQuery = useMemo(
    () => ({
      search: search.trim() || undefined,
      category:
        categoryFilter === 'all'
          ? undefined
          : categories.find((c) => c.id === categoryFilter)?.code,
    }),
    [search, categoryFilter, categories],
  );

  const pestsQ = usePestsInfinite(query);
  const createMut = useCreatePest();
  const updateMut = useUpdatePest();
  const archiveMut = useArchivePest();
  const mergeMut = useMergePests();

  const items = pestsQ.data?.items ?? [];
  const total = pestsQ.data?.total ?? 0;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<PestListItem | null>(null);

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (p: PestListItem) => {
    setEditing(p);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: PestPayload) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
      toast.success(`Вредитель «${payload.name_translations.ru}» сохранён`);
    } else {
      await createMut.mutateAsync(payload);
      toast.success(`Вредитель «${payload.name_translations.ru}» создан`);
    }
  };

  const pickerTargetCandidate = useMemo<MergeCandidate | null>(() => {
    if (!pickerTarget) return null;
    return {
      id: pickerTarget.id,
      label: pickerTarget.name_translations.ru ?? pickerTarget.latin_name ?? '—',
      sublabel: pickerTarget.latin_name ?? undefined,
    };
  }, [pickerTarget]);

  const pickerCandidates = useMemo<MergeCandidate[]>(
    () =>
      items
        .filter((p) => p.id !== pickerTarget?.id)
        .map((p) => ({
          id: p.id,
          label: p.name_translations.ru ?? p.latin_name ?? '—',
          sublabel: p.latin_name ?? undefined,
        })),
    [items, pickerTarget?.id],
  );

  const handlePickerPreview = useCallback(
    async (sourceId: string, targetId: string) => {
      const res = await mergeMut.mutateAsync({ sourceId, targetId, dryRun: true });
      return { totalRepointed: res.total_repointed, totalDeleted: res.total_deleted };
    },
    [mergeMut.mutateAsync],
  );

  const handlePickerConfirm = useCallback(
    async (sourceId: string, targetId: string) => {
      const label = pickerTarget?.name_translations.ru ?? 'эталон';
      try {
        await mergeMut.mutateAsync({ sourceId, targetId, dryRun: false });
        toast.success(`Поглощено в «${label}»`);
      } catch (err) {
        toast.error((err as Error).message);
        throw err;
      }
    },
    [mergeMut.mutateAsync, pickerTarget],
  );

  const handleArchive = async (p: PestListItem) => {
    if (!window.confirm(`Архивировать «${p.name_translations.ru}»?`)) return;
    try {
      await archiveMut.mutateAsync(p.id);
      toast.success('Архивировано.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<PestListItem>[] = [
    {
      key: 'name',
      header: 'Название (RU)',
      render: (p) => (
        <button
          type="button"
          onClick={() => handleEdit(p)}
          className="text-left font-medium text-foreground hover:text-primary"
        >
          {p.name_translations.ru ?? p.latin_name ?? '—'}
        </button>
      ),
    },
    {
      key: 'latin',
      header: 'Латинское',
      width: '180px',
      render: (p) => (
        <span className="text-sm italic text-muted-foreground">
          {p.latin_name ?? '—'}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Категория',
      width: '150px',
      render: (p) =>
        p.category_name ? (
          <Badge variant="secondary">{p.category_name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'order',
      header: 'Отряд / семейство',
      width: '180px',
      render: (p) => (
        <span className="text-sm italic text-muted-foreground">
          {[p.order_latin, p.family_latin].filter(Boolean).join(' / ') || '—'}
        </span>
      ),
    },
    {
      key: 'gen',
      header: 'Поколений/год',
      width: '120px',
      render: (p) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {p.generations_per_year ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (p) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${p.name_translations.ru}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => handleEdit(p)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { setPickerTarget(p); setPickerOpen(true); }}>
              <GitMerge className="h-4 w-4" />
              Поглотить
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void handleArchive(p)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<PestListItem>
        title="Вредители"
        subtitle={`Системный справочник · read-only baseline для всех хозяйств · ${total} ${plural(total)}`}
        createLabel="Создать вредителя"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию или латыни…"
        items={items}
        isLoading={pestsQ.isLoading}
        isError={pestsQ.isError}
        error={pestsQ.error ?? undefined}
        onRetry={() => pestsQ.refetch()}
        getRowKey={(p) => p.id}
        total={total}
        unitLabel="вредителей"
        hasMore={!!pestsQ.hasNextPage}
        loadingMore={pestsQ.isFetchingNextPage}
        onLoadMore={() => pestsQ.fetchNextPage()}
        columns={columns}
        emptyTitle="Пока нет ни одного вредителя"
        emptyHint="Создайте первую запись справочника вредителей — название, категория и стадии станут доступны всем хозяйствам."
        toolbarSlot={
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все категории</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name_translations.ru ?? c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <PestEditorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        pest={editing}
        categories={categories}
        onSubmit={handleSubmit}
      />

      <ReferenceMergeDialog
        mode="picker"
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        target={pickerTargetCandidate}
        candidates={pickerCandidates}
        entityAccusative="вредителя"
        onPreview={handlePickerPreview}
        onConfirm={handlePickerConfirm}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminPestsPage;
